import { setUserObject, getUserObject, setDefaultFilterSessionKey } from "../../Shared/scriptts/session/sessionmain";
import { params, signinClick, hasKeywordOrFilter, aliasMapping, brandedSearch, displayResults, executeSearchUrl, isSavedSearch } from '../search-main';
import { resetOrganizationFilter } from "./search-filter-organization";
import { resetSeriesFilter } from "./search-filter-series";
import { getRequestVerificationToken, loadAlertBanner } from "../../Shared/scriptts/utilities";
import { Params } from "../../Shared/scriptts/search-params";
import { UserObject, alertBannerOptions } from "../../Shared/scriptts/interfaces";

const defaultFilterContainer = document.querySelector<HTMLDivElement>('#default-filter-section');
const defaultFilterBtn = document.querySelector<HTMLButtonElement>('#default-filter-btn');
const defaultFilterBtnLbl = document.querySelector<HTMLSpanElement>('#default-filter-btn-lbl');
const defaultFilterToggleContainer = document.querySelector<HTMLDivElement>('#default-filter-toggle-container');
const defaultFilterToggle = document.querySelector<HTMLButtonElement>('#default-filter-toggle');
const defaultFilterToggleSwitch = document.querySelector<HTMLButtonElement>('#default-filter-toggle-switch');
const defaultFilterCount = document.querySelector<HTMLSpanElement>('#default-filter-count');
const deleteDefaultFilterBtn = document.querySelector<HTMLButtonElement>('#delete-default-filter');
const defaultFilterTitle = document.querySelector<HTMLSpanElement>('#default-filter-title');
const defaultFilterTitle2 = document.querySelector<HTMLSpanElement>('#default-filter-title2');
const defaultFilterAlertContainer = document.querySelector<HTMLElement>('#default-filter-alert-container');
const updateDefaultFilterText = "Update default filters";
const setDefaultFilterText = "Set as default filters";
let filterCount = "0";
let deletedDefaultFilters: Params | undefined;

export async function initSetDefaultFilterInSession() {
    const setDefaultFilterSession = sessionStorage.getItem(setDefaultFilterSessionKey);
    const userObject: UserObject = getUserObject();
    if (setDefaultFilterSession) {
        sessionStorage.removeItem(setDefaultFilterSessionKey);
        try {
            await setDefaultFilters(userObject, params);
        } catch (error) {
            console.error("Failed to set default filters:", error);
        }
    } else {
        filterCount = getDefaultFilterCount(userObject.DefaultFilters);
    }

    if (userObject.UseDefaultFilters ?? true) {
        addDefaultFiltersToParams(userObject.DefaultFilters);
    }
}
    
defaultFilterBtn?.addEventListener('click', async (event) => {
    const userObject: UserObject = getUserObject();
    if (!userObject.IsAuthenticated) {
        sessionStorage.setItem(setDefaultFilterSessionKey, "true");
        signinClick(event);
        return;
    } else {
        await setDefaultFilters(userObject, params);
    }
});

deleteDefaultFilterBtn?.addEventListener('click', async (event) => {
    await deleteDefaultFilters();
});

defaultFilterToggleSwitch?.addEventListener('click', () => {
    const userObject: UserObject = getUserObject();
    userObject.UseDefaultFilters = !(userObject.UseDefaultFilters ?? true);
    setUserObject(userObject);
    if (userObject.UseDefaultFilters) {
        addDefaultFiltersToParams(userObject.DefaultFilters);
    } else {
        removeDefaultFiltersFromParams(userObject.DefaultFilters);
    }
    displayDefaultFilterToggleContainer(userObject);
    defaultFilterToggle?.click();
    displayResults(executeSearchUrl, false, true);
});

async function setDefaultFilters(userObject: UserObject, filterParams: Params) {
    const requestVerificationToken = getRequestVerificationToken();
    const response = await fetch('/Search/SetDefaultFilters', {
        method: 'POST',
        headers: {
            "Content-Type": "application/json; charset=utf-8",
            ...(requestVerificationToken ? { "RequestVerificationToken": requestVerificationToken } : {})
        },
        cache: "no-cache",
        credentials: 'same-origin',
        body: JSON.stringify(filterParams)
    });
    if (!response.ok) {
        console.error(response.statusText);
    }
    else {
        userObject.DefaultFilters = await response.json();
        filterCount = getDefaultFilterCount(userObject.DefaultFilters);
        setUserObject(userObject);
        displayDefaultFilters(true);
        loadSetDefaultFilterAlert();
    }
}

async function deleteDefaultFilters() {
    const userObject: UserObject = getUserObject();
    const requestVerificationToken = getRequestVerificationToken();
    const response = await fetch('/Search/DeleteDefaultFilters', {
        method: 'POST',
        headers: {
            "Content-Type": "application/json; charset=utf-8",
            ...(requestVerificationToken ? { "RequestVerificationToken": requestVerificationToken } : {})
        },
        cache: "no-cache",
        credentials: 'same-origin'
    });
    if (!response.ok) {
        console.error(response.statusText);
    }
    else {
        filterCount = getDefaultFilterCount(userObject.DefaultFilters);
        deletedDefaultFilters = userObject.DefaultFilters;
        removeDefaultFiltersFromParams(userObject.DefaultFilters);
        delete userObject.DefaultFilters;
        delete userObject.UseDefaultFilters;
        setUserObject(userObject);
        displayDefaultFilters();
        loadDeleteDefaultFilterAlert();
        resetOrganizationFilter();
        resetSeriesFilter();
        displayResults(executeSearchUrl, false, true);
    }
}

export function removeDefaultFiltersFromParams(defaultFilters?: Params) {
    if (!defaultFilters) { return; }

    (Object.keys(defaultFilters) as (keyof Params)[]).forEach((key) => {
        const defaultValue = defaultFilters[key];
        const currentValue = params[key];

        if (Array.isArray(defaultValue) && Array.isArray(currentValue)) {
            (params as Partial<Params>)[key] = currentValue.filter((item) => !defaultValue.includes(item)) as never;
        }
        else if (defaultValue !== undefined && currentValue === defaultValue) {
            delete params[key];
        }
    });
}

export function addDefaultFiltersToParams(defaultFilters?: Params) {
    if (!defaultFilters) return;
    (Object.keys(defaultFilters) as (keyof Params)[]).forEach((key) => {
        const defaultValue = defaultFilters[key];
        const currentValue = (params as any)[key];

        if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
            defaultValue.forEach((item) => {
                if (!currentValue.includes(item)) {
                    currentValue.push(item);
                }
            });
        } else if (!(key in params) || (params as any)[key] === undefined) {
            (params as any)[key] = defaultValue;
        }
    });
}

export function displayDefaultFilters(hide: boolean = false) {
    var userObject: UserObject = getUserObject();
    if (hide || !hasKeywordOrFilter(true) || isSavedSearch || brandedSearch) {
        hideDefaultFiltersContainer();
    } else if (!userObject.DefaultFilters) {
        if (defaultFilterBtnLbl) {
            defaultFilterBtnLbl.innerText = setDefaultFilterText;
        }
        showDefaultFiltersContainer();
    } else if (isDefaultFiltersDifferent(userObject)) {
        if (defaultFilterBtnLbl) {
            defaultFilterBtnLbl.innerText = updateDefaultFilterText;
        }
        showDefaultFiltersContainer();
    } else {
        hideDefaultFiltersContainer();
    }

    if (userObject.DefaultFilters && !isSavedSearch && !brandedSearch) {
        displayDefaultFilterToggleContainer(userObject);
    }
    else {
        hideDefaultFilterToggleContainer();
    }
}

function isDefaultFiltersDifferent(userObject: UserObject): boolean {
    if (!userObject.DefaultFilters) { return true; }
    const relevantKeys = new Set([
        ...Object.keys(params),
        ...Object.keys(userObject.DefaultFilters)
    ]);

    for (const key of relevantKeys) {
        if (key === "Keyword" || !(key in aliasMapping) || !aliasMapping[key].SearchFilter) {
            continue;
        }

        const currentDefault = userObject.DefaultFilters[key] ?? null;
        const currentParam = params[key] ?? null;
        const currentParamIsArray = Array.isArray(currentParam);
        const currentDefaultIsArray = Array.isArray(currentDefault);

        if (currentParamIsArray || currentDefaultIsArray) {
            const normalizedParam = currentParamIsArray ? currentParam : [];
            const normalizedDefault = currentDefaultIsArray ? currentDefault : [];

            if (!arraysEqual(normalizedParam, normalizedDefault)) {
                return true;
            }
        }
        else if (currentDefault !== currentParam) {
            return true;
        }
    }

    return false;
}

function arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;

    const normalizedArr1 = [...arr1].map(String).sort();
    const normalizedArr2 = [...arr2].map(String).sort();

    return normalizedArr1.every((val, idx) => val === normalizedArr2[idx]);
}

function displayDefaultFilterToggleContainer(userObject: UserObject) {
    defaultFilterToggleContainer?.classList.remove("hidden");
    if (defaultFilterCount) {
        defaultFilterCount.innerText = filterCount;
    }
    if (defaultFilterToggleSwitch && defaultFilterTitle && defaultFilterTitle2) {
        const useDefaultFilters = userObject.UseDefaultFilters ?? true;
        const defaultFilterToggleIcon = `toggle_${useDefaultFilters ? 'on' : 'off'}`;
        const defaultFilterTitleDisplay = `${!useDefaultFilters ? 'Show' : 'Hide'} default filters`;
        defaultFilterTitle.innerText = defaultFilterTitleDisplay
        defaultFilterTitle2.innerText = defaultFilterTitleDisplay;
        defaultFilterToggleSwitch.innerHTML = `
            <svg class="w-9 h-9 inline-block mr-1 fill-${useDefaultFilters ? 'success' : 'gray'}" role="presentation"><use href="/img/icons/sprite.svg#${defaultFilterToggleIcon}"></use></svg>
            Show default filters`;
    }
}

function hideDefaultFilterToggleContainer() {
    defaultFilterToggleContainer?.classList.add("hidden");
}

function getDefaultFilterCount(defaultFilters: Params | undefined): string {
    if (!defaultFilters) { return "0"; }
    let defaultFilterCount = 0;
    for (const key in defaultFilters) {
        if (key === 'GradeSearch' || key === 'ExcludeOrganizations') { continue; }
        if (Array.isArray(defaultFilters[key])) {
            defaultFilterCount += defaultFilters[key].length
        }
        else {
            defaultFilterCount++;
        }
    }
    return defaultFilterCount.toString();
}

function showDefaultFiltersContainer() {
    defaultFilterContainer?.classList.remove('hidden');
}

function hideDefaultFiltersContainer() {
    defaultFilterContainer?.classList.add('hidden');
}

function loadSetDefaultFilterAlert() {
    if (!defaultFilterAlertContainer) { return; };
    loadAlertBanner(defaultFilterAlertContainer, getSetDefaultFilterAlertContent()).then(() => {
        bindSetAlertEvents();
    });
}

function loadDeleteDefaultFilterAlert() {
    if (!defaultFilterAlertContainer) { return; };
    loadAlertBanner(defaultFilterAlertContainer, getDeleteDefaultFilterAlertContent()).then(() => {
        bindDeleteAlertEvents();
    });
}

function getSetDefaultFilterAlertContent(): alertBannerOptions {
    return {
        alertType: 'Success',
        isDismissable: true,
        message: `
            You set ${filterCount} filter(s) as your default filter(s). You will see these filters every time you sign in.
            <button type="button" class="button button-unstyled" id="delete-default-filters-alert-btn">Delete default filters</button>`
    };
}

function getDeleteDefaultFilterAlertContent(): alertBannerOptions {
    return {
        alertType: 'Error',
        isDismissable: true,
        message: `
            You deleted ${filterCount} default filter(s). You will no longer see these filters every time you sign in.
            <button type="button" class="button button-unstyled" id="undo-default-filters-alert-btn">Undo</button>`
    };
}

function bindSetAlertEvents() {
    var deleteDefaultFiltersAlertBtn = document.querySelector<HTMLButtonElement>('#delete-default-filters-alert-btn');
    deleteDefaultFiltersAlertBtn?.addEventListener('click', async (event) => {
        await deleteDefaultFilters();
    });
}

function bindDeleteAlertEvents() {
    var undoDefaultFiltersAlertBtn = document.querySelector<HTMLButtonElement>('#undo-default-filters-alert-btn');
    undoDefaultFiltersAlertBtn?.addEventListener('click', async (event) => {
        let userObject = getUserObject();
        userObject.DefaultFilters = deletedDefaultFilters;
        addDefaultFiltersToParams(deletedDefaultFilters);
        await setDefaultFilters(userObject, userObject.DefaultFilters ?? params);
        displayResults(executeSearchUrl, false, true);
    });
}
